// src/app/pages/coalitions/coalitions.component.ts

import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { EmailJsService } from '../../services/emailjs.service';
import { SeoService } from '../../services/seo.service';

export interface Coalition {
  id: string;
  name: string;
  icon: string;
  tagline: string;
  description: string;
  why: string;
}

@Component({
  selector: 'app-coalitions',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './coalitions.component.html',
  styleUrls: ['./coalitions.component.css'],
})
export class CoalitionsComponent {
  coalitions: Coalition[] = [
    {
      id: 'tech',
      name: 'Tech Workers for Lydia',
      icon: '💻',
      tagline: 'A technologist in the Senate.',
      description: 'Developers, engineers, designers, and digital workers who know that Congress needs people who actually understand technology - net neutrality, digital rights, crypto policy, and AI governance.',
      why: 'Lydia built this campaign website herself. She understands the stack, not just the talking points.',
    },
    {
      id: 'environment',
      name: 'Environmental Advocates for Lydia',
      icon: '🌊',
      tagline: 'Protect the Great Lakes. Protect Michigan.',
      description: 'Conservationists, outdoors enthusiasts, and everyday Michiganders who believe our water, air, and land are non-negotiable - not bargaining chips for party politics.',
      why: 'Lydia\'s home in Ironwood is surrounded by the forests, rivers, and lakes she is fighting to protect.',
    },
    {
      id: 'lgbtq',
      name: 'LGBTQ+ Community for Lydia',
      icon: '🏳️‍🌈',
      tagline: 'Every Michigander deserves to be unchained.',
      description: 'LGBTQ+ Michiganders and allies who believe in full equality, dignity, and safety for every person in our state - no exceptions, no compromise.',
      why: 'Lydia is a vocal advocate for LGBTQ+ rights and is co-hosting the Lights of Pride 2026 event.',
    },
    {
      id: 'veterans',
      name: 'Veterans for Lydia',
      icon: '🎖️',
      tagline: 'Honor the service. Fix the system.',
      description: 'Veterans and military families who have given everything for this country and deserve a Senator who fights just as hard for them - on healthcare, benefits, housing, and opportunity.',
      why: 'Michigan\'s UP has deep military roots. Lydia will never treat veterans as a political prop.',
    },
    {
      id: 'educators',
      name: 'Educators for Lydia',
      icon: '🎓',
      tagline: 'Our schools deserve better.',
      description: 'Teachers, professors, school staff, and education advocates who believe every Michigan child deserves a fully-funded, world-class education - not a system starved by political gridlock.',
      why: 'Lydia knows that strong communities start with strong schools.',
    },
    {
      id: 'small-business',
      name: 'Small Business Owners for Lydia',
      icon: '🏪',
      tagline: 'Michigan\'s economy runs on main street.',
      description: 'Independent business owners, entrepreneurs, and tradespeople who are tired of policies written for corporations and lobbyists - not the people who actually create jobs in Michigan communities.',
      why: 'As a Libertarian, Lydia answers to her constituents - not corporate donors.',
    },
    {
      id: 'outdoors',
      name: 'Outdoor Michigan for Lydia',
      icon: '🦌',
      tagline: 'Keep Michigan wild.',
      description: 'Hunters, anglers, hikers, campers, and UP residents who want a Senator who will protect public lands, clean water, and the outdoor heritage that makes Michigan one of a kind.',
      why: 'Lydia grew up surrounded by the UP\'s wild beauty. Protecting it isn\'t a policy position - it\'s personal.',
    },
    {
      id: 'healthcare',
      name: 'Healthcare Workers for Lydia',
      icon: '🩺',
      tagline: 'The system is broken. Let\'s fix it.',
      description: 'Nurses, doctors, social workers, and healthcare staff who see every day what happens when a broken system puts profit over patients - and are ready for a Senator who will fight to change it.',
      why: 'Lydia supports expanding Medicaid access and fighting for affordable care for every Michigander.',
    },
  ];

  selectedCoalition: string = '';
  form = { name: '', email: '', zip: '', message: '' };
  submitting = false;
  submitted = false;
  error = '';

  activeCard: string | null = null;

  private readonly baseUrl =
    'https://www.lydialynnchristensen.com';

  private readonly coalitionsPageId =
    `${this.baseUrl}/coalitions#coalitions`;

  private readonly coalitionListId =
    `${this.baseUrl}/coalitions#coalition-list`;

  constructor(
    private emailJs: EmailJsService,
    private seo: SeoService
  ) {}

  ngOnInit(): void {
    this.seo.update(
      'Coalitions',
      'Join a coalition of Michiganders fighting for change with Lydia. Tech workers, veterans, educators, environmentalists, and more.',
      'coalitions'
    );

    this.seo.setStructuredData({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "CollectionPage",
          "@id": this.coalitionsPageId,
          "url": `${this.baseUrl}/coalitions`,
          "name": "Lydia Christensen Coalitions",
          "description": "Coalitions of supporters organized around areas of interest and community.",
          "isPartOf": { "@id": this.seo.websiteId },
          "about": { "@id": this.seo.lydiaId },
          "publisher": { "@id": this.seo.campaignId },
          "mainEntity": { "@id": this.coalitionListId },
          "inLanguage": "en-US"
        },
        {
          "@type": "ItemList",
          "@id": this.coalitionListId,
          "name": "Lydia Christensen Coalitions",
          "numberOfItems": this.coalitions.length,
          "itemListOrder": "https://schema.org/ItemListOrderAscending",
          "itemListElement": this.coalitions.map(
            (coalition, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "item": {
                "@type": "Organization",
                "@id": `${this.baseUrl}/coalitions#${coalition.id}`,
                "name": coalition.name,
                "description": coalition.description,
                "memberOf": {
                  "@id": this.seo.campaignId
                },
                "potentialAction": {
                  "@type": "JoinAction",
                  "@id": `${this.baseUrl}/coalitions#join-${coalition.id}`,
                  "name": `Join ${coalition.name}`,
                  "agent": {
                    "@id": this.seo.lydiaId
                  },
                  "object": {
                    "@id": `${this.baseUrl}/coalitions#${coalition.id}`
                  },
                  "target": {
                    "@type": "EntryPoint",
                    "urlTemplate": `${this.baseUrl}/coalitions#join-form`,
                    "inLanguage": "en-US"
                  }
                }
              }
            })
          )
        }
      ]
    });
  }

  toggleCard(id: string): void {
    this.activeCard = this.activeCard === id ? null : id;
  }

  scrollToJoin(coalitionId: string): void {
    this.selectedCoalition = coalitionId;
    const el = document.getElementById('join-form');
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }

  onSubmit(): void {
    this.submitting = true;
    this.error = '';

    const coalition = this.coalitions.find(
      c => c.id === this.selectedCoalition
    );

    const data = {
      name: this.form.name,
      email: this.form.email,
      zip: this.form.zip,
      role: `Coalition Signup: ${coalition?.name ?? this.selectedCoalition}`,
      message:
        this.form.message ||
        `Joined ${coalition?.name ?? 'a coalition'}`,
      form_type: 'Coalition Signup',
    };

    this.emailJs
      .sendEmail(data, 'Coalition Signup')
      .then(() => {
        this.submitted = true;
        this.submitting = false;
      })
      .catch(() => {
        this.error = 'Something went wrong. Please try again.';
        this.submitting = false;
      });
  }

  reset(): void {
    this.form = {
      name: '',
      email: '',
      zip: '',
      message: ''
    };

    this.selectedCoalition = '';
    this.submitted = false;
    this.error = '';
  }

  get selectedCoalitionName(): string {
    return (
      this.coalitions.find(
        c => c.id === this.selectedCoalition
      )?.name ?? ''
    );
  }
}